import { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabase';
import { Link, useSearchParams } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import '../styles.css';
import DownloadPromptModal from '../components/DownloadPromptModal';
import { handleSongDownload } from '../utils/downloadHelper';

function Library() {
  const [songs, setSongs] = useState([]);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptType, setPromptType] = useState('register');
  const [selectedSong, setSelectedSong] = useState(null);
  const [user, setUser] = useState(null);
  const [downloadCountThisMonth, setDownloadCountThisMonth] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // For live suggestions
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Suggestions dropdown effect
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }
    const matches = songs
      .filter(
        (song) =>
          song.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (song.composer && song.composer.toLowerCase().includes(debouncedSearch.toLowerCase()))
      )
      .slice(0, 5)
      .map((song) => ({
        type: 'song',
        title: song.title,
        id: song.id,
        permalink: song.permalink,
      }));
    setSuggestions(matches);
    setSelectedSuggestionIndex(-1);
  }, [debouncedSearch, songs]);

  // If no ?page= param, set it to 1 on initial load
  useEffect(() => {
    if (!searchParams.get('page')) {
      setSearchParams({ page: '1' }, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Restore scroll position when landing here
  useEffect(() => {
    sessionStorage.setItem('lastLibraryPage', currentPage);
    const lastPage = parseInt(sessionStorage.getItem('lastLibraryPage'), 10);
    const lastScroll = parseInt(sessionStorage.getItem('lastLibraryScroll'), 10);

    if (lastScroll && lastPage === currentPage) {
      setTimeout(() => {
        window.scrollTo(0, lastScroll);
        sessionStorage.removeItem('lastLibraryScroll');
      }, 100);
    }
    // eslint-disable-next-line
  }, [currentPage]);

  // Pagination handler
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setSearchParams({ page: page.toString() });
      sessionStorage.setItem('lastLibraryPage', page);
      setTimeout(() => window.scrollTo(0, 0), 50);
    }
  };

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      // Optionally fetch download count here
    };
    getUser();
  }, []);

  useEffect(() => {
    const fetchSongsAndAuth = async () => {
      const { data: songData, error: songError } = await supabase
        .from('songs')
        .select('id, title, composer, google_drive_file_id, permalink, is_public, downloads, created_at, audio_url')
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (songError) {
        setError('Failed to load songs.');
      } else {
        setSongs(songData || []);
      }
      const { data: sessionData } = await supabase.auth.getSession();
      setIsAuthenticated(!!sessionData?.session);
    };
    fetchSongsAndAuth();
  }, [sortBy, sortOrder]);

  // Filter and paginate
  const filteredSongs = songs.filter(song =>
    song.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    (song.composer && song.composer.toLowerCase().includes(debouncedSearch.toLowerCase()))
  );

  const totalItems = filteredSongs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSongs = filteredSongs.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setSearchParams({ page: '1' });
    sessionStorage.setItem('lastLibraryPage', 1);
    setTimeout(() => window.scrollTo(0, 0), 50);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const honeypot = e.target.elements.honeypot.value;
    if (honeypot) {
      setError('Spam detected');
      return;
    }

    // Advanced logic: If empty search, show error and do nothing
    if (!searchTerm.trim()) {
      setError('Please enter a search term.');
      return;
    }

    // Track search in GA4
    if (window.gtag) {
      window.gtag('event', 'library_search', {
        event_category: 'Library Search',
        event_label: searchTerm,
        search_term: searchTerm,
        value: 1,
      });
    }

    // Reset suggestions and pagination
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setSearchParams({ page: '1' }); // Reset to first page of results

    // Optionally blur search field for mobile UX
    if (searchInputRef.current) searchInputRef.current.blur();

    setError(null); // Clear any error message
  };

  // Download/share/modal logic...
  const handleDownload = async (song) => {
    setSelectedSong(song);
    // Use the centralized helper
    const result = await handleSongDownload(song, user, setPromptType, setShowPrompt);

    if (!result.allowed) return; // prompt already shown

    window.open(`https://drive.google.com/uc?export=download&id=${song.google_drive_file_id}`, '_blank');

    // Update download count in UI immediately
    setSongs(prev => prev.map(s => s.id === song.id ? { ...s, downloads: (s.downloads || 0) + 1 } : s));
  };

  const handleShare = (songTitle, songPermalink) => {
    const shareUrl = `${window.location.origin}/song/${songPermalink}?fromPage=${currentPage}`;
    const shareText = `Check out "${songTitle}" on Choir Center!`;
    if (navigator.share) {
      navigator.share({ title: songTitle, text: shareText, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => alert('Link copied to clipboard! Share it manually.'))
        .catch(() => {});
    }
  };

  // Pagination UI helper
  const getPaginationRange = () => {
    const range = [];
    const maxVisiblePages = 5;
    const sideRange = 2;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      range.push(1);
      const startRange = Math.max(2, currentPage - sideRange);
      if (startRange > 2) range.push('...');
      for (let i = startRange; i <= Math.min(totalPages - 1, currentPage + sideRange); i++) {
        range.push(i);
      }
      const endRange = Math.min(totalPages - 1, currentPage + sideRange);
      if (endRange < totalPages - 1) range.push('...');
      if (totalPages > 1) range.push(totalPages);
    }
    return range;
  };

  const paginationRange = getPaginationRange();

  const handleRegister = () => {
    setShowPrompt(false);
  };

  const handleSubscribe = () => {
    setShowPrompt(false);
    window.open('https://choircenter.com/subscribe', '_blank');
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.title);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setSearchParams({ page: '1' });
    if (searchInputRef.current) searchInputRef.current.blur();
  };

  return (
    <div className="library-container">
      <section className="ad-section">
        <AdBanner position="other_pages_below_header" />
      </section>
      <h1 className="library-title text-2xl font-bold text-green-900 text-center my-4">Song Library</h1>
      <p className="library-description text-center text-green-800 mb-6">
        Explore our extensive collection of free choir sheet music, available for download and sharing. Sort and search to find the perfect pieces for your choir.
      </p>

      {/* --- SIMPLE SEARCH/SORT BAR --- */}
      <form onSubmit={handleFilterSubmit} className="filter-bar max-w-xl mx-auto my-3 flex flex-col sm:flex-row gap-3 items-center">
        <input
          type="text"
          placeholder="Search library..."
          value={searchTerm}
          ref={searchInputRef}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-4 py-3 rounded-xl border border-green-400 bg-white focus:ring-2 focus:ring-lime-400 text-green-900 text-lg shadow font-medium"
          autoComplete="off"
          style={{ fontFamily: `'Inter', 'Sora', sans-serif'` }}
        />
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="filter-select"
        >
          <option value="created_at">Sort by Date</option>
          <option value="downloads">Sort by Downloads</option>
          <option value="title">Sort by Title</option>
        </select>
        <select
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          className="filter-select"
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
        <input type="text" name="honeypot" className="honeypot" />
      </form>
      {/* --- SIMPLE SUGGESTIONS --- */}
      {suggestions.length > 0 && (
        <ul className="max-w-xl mx-auto mt-1 border border-green-400 bg-white rounded-xl shadow text-green-900">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-4 py-2 cursor-pointer hover:bg-lime-100 ${
                index === selectedSuggestionIndex ? 'bg-lime-200 text-green-800' : ''
              }`}
              onMouseEnter={() => setSelectedSuggestionIndex(index)}
              onMouseLeave={() => setSelectedSuggestionIndex(-1)}
            >
              <span className="font-semibold">{suggestion.title}</span>
            </li>
          ))}
        </ul>
      )}

      {totalItems > itemsPerPage && (
        <div className="pagination-top">
          <div className="pagination-controls">
            <label htmlFor="itemsPerPage">Items per page:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="pagination-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            {paginationRange.map((item, index) => (
              item === '...' ? (
                <span key={index} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => handlePageChange(item)}
                  className={`pagination-button ${currentPage === item ? 'active' : ''}`}
                >
                  {item}
                </button>
              )
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      {songs.length === 0 && !error ? (
        <p>No songs available.</p>
      ) : (
        <div className="song-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedSongs.map((song) => (
            <div
              key={song.id}
              className="flex flex-col justify-between rounded-2xl border border-green-200 shadow bg-white cursor-pointer transition hover:shadow-lg"
              onClick={() => {
                sessionStorage.setItem('lastLibraryScroll', window.scrollY.toString());
                sessionStorage.setItem('lastLibraryPage', currentPage);
              }}
            > 
              {/* Fire GA4 event when a user clicks a song card to view its details */}
              <Link
                to={`/song/${song.permalink || song.id}?fromPage=${currentPage}`}
                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'song_card_click', {
                      event_category: 'Song Library',
                      event_label: song?.title || 'Unknown Song',
                      song_id: song?.id,
                      song_permalink: song?.permalink,
                      value: 1,
                    });
                  }
                }}
              >
                <div className="p-4 pb-3">
                  <h2 className="font-bold text-lg md:text-xl text-green-900 mb-1 text-center">
                    {song.title}
                  </h2>
                  <p className="text-green-700 text-sm mb-2 text-center">
                    {song.composer || 'Unknown Composer'}
                  </p>
                  {song.audio_url && (
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-lime-50 text-lime-700 text-xs font-semibold border border-lime-200">
                        <span className="mr-1">Audio Available</span>
                        {/* Headphone Icon */}
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <path d="M4 13v-2a8 8 0 1 1 16 0v2" stroke="#a3e635" strokeWidth="1.5" fill="none"/>
                          <rect x="3" y="13" width="4" height="6" rx="2" fill="#a3e635" />
                          <rect x="17" y="13" width="4" height="6" rx="2" fill="#a3e635" />
                        </svg>
                      </span>
                    </div>
                  )}
                  <p className="text-green-700/80 text-xs mb-1 text-center">
                    Downloaded <b>{song.downloads || 0}</b> times
                  </p>
                  <p className="text-green-700/60 text-xs text-center">
                    Added {song.created_at
                      ? new Date(song.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Unknown Date'}
                  </p>
                </div>
              </Link>
              <div className="flex gap-2 px-4 pb-3">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (window.gtag) {
                      gtag('event', 'file_download', {
                        event_category: 'Downloads',
                        event_label: song?.title || 'Library Song',
                        value: 1,
                      });
                    }
                    handleDownload(song);
                  }}
                  className="flex-1 py-1 rounded-xl bg-gradient-to-br from-green-600 via-lime-400 to-green-400 text-black text-sm font-bold shadow hover:scale-105 transition-all"
                >
                  Download
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleShare(song.title, song.permalink || song.id);
                  }}
                  className="flex-1 py-1 rounded-xl bg-gradient-to-br from-black/80 via-green-600 to-lime-600 text-lime-100 text-sm font-bold shadow hover:scale-105 transition-all"
                >
                  Share
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalItems > itemsPerPage && (
        <div className="pagination-bottom">
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            {paginationRange.map((item, index) => (
              item === '...' ? (
                <span key={index} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => handlePageChange(item)}
                  className={`pagination-button ${currentPage === item ? 'active' : ''}`}
                >
                  {item}
                </button>
              )
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Download Prompt Modal */}
      {showPrompt && (
        <DownloadPromptModal
          onClose={() => setShowPrompt(false)}
          onRegister={handleRegister}
          onSubscribe={handleSubscribe}
        />
      )}
    </div>
  );
}

export default Library;
